<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import InboxesAPI from 'dashboard/api/inboxes';
import WhatsappBusinessManagementToken from './WhatsappBusinessManagementToken.vue';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappBusinessManagementToken,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { runEmbeddedSignup } = useWhatsappEmbeddedSignup();
    return { v$: useVuelidate(), runEmbeddedSignup };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      isReconfiguring: false,
      vitalpbxConfig: {
        extensionNumber: '',
        deviceUser: '',
        devicePassword: '',
        wssUrl: '',
        baseUrl: '',
        tenant: '',
        sipDomain: '',
        phoneNumber: '',
        outboundCallerId: '',
        amiHost: '',
        amiPort: '',
        amiUsername: '',
        amiSecret: '',
        validationStatus: '',
        isPasswordRevealed: false,
        isLoading: false,
        isVerifying: false,
        verificationMessage: '',
        verificationSuccess: null,
      },
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    showWhatsAppReconfigure() {
      return (
        this.isEmbeddedSignupWhatsApp &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          this.isOnChatwootCloud
            ? FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP_FLOW
            : FEATURE_FLAGS.WHATSAPP_RECONFIGURE
        )
      );
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
    if (this.isAVitalpbxChannel) {
      this.fetchVitalpbxConfig();
    }
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async reconfigureWhatsApp() {
      this.isReconfiguring = true;
      try {
        const credentials = await this.runEmbeddedSignup();
        // User dismissed the Meta popup without completing signup.
        if (!credentials) return;

        await whatsappChannel.reauthorizeWhatsApp({
          inboxId: this.inbox.id,
          ...credentials,
        });
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_SUCCESS')
        );
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_ERROR')
        );
      } finally {
        this.isReconfiguring = false;
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async fetchVitalpbxConfig(reveal = false) {
      this.vitalpbxConfig.isLoading = true;
      try {
        const { data } = await InboxesAPI.getVitalpbxConfiguration(
          this.inbox.id,
          reveal
        );
        this.vitalpbxConfig.extensionNumber =
          data.extension_number || this.inbox.extension_number || '';
        this.vitalpbxConfig.deviceUser = data.device_user || '';
        this.vitalpbxConfig.devicePassword = data.device_password || '';
        this.vitalpbxConfig.wssUrl = data.wss_url || '';
        this.vitalpbxConfig.baseUrl = data.base_url || '';
        this.vitalpbxConfig.tenant = data.tenant || '';
        this.vitalpbxConfig.sipDomain = data.sip_domain || '';
        this.vitalpbxConfig.phoneNumber =
          data.phone_number || this.inbox.phone_number || '';
        this.vitalpbxConfig.outboundCallerId = data.outbound_caller_id || '';
        this.vitalpbxConfig.amiHost = data.ami_host || '';
        this.vitalpbxConfig.amiPort = data.ami_port || '';
        this.vitalpbxConfig.amiUsername = data.ami_username || '';
        this.vitalpbxConfig.amiSecret = data.ami_secret || '';
        this.vitalpbxConfig.validationStatus = data.validation_status || '';
        this.vitalpbxConfig.isPasswordRevealed = reveal;
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            error.response?.data?.error ||
            this.$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.FETCH_ERROR')
        );
      } finally {
        this.vitalpbxConfig.isLoading = false;
      }
    },
    async toggleRevealPassword() {
      const nextReveal = !this.vitalpbxConfig.isPasswordRevealed;
      await this.fetchVitalpbxConfig(nextReveal);
    },
    async verifyVitalpbxConnection() {
      this.vitalpbxConfig.isVerifying = true;
      this.vitalpbxConfig.verificationMessage = '';
      this.vitalpbxConfig.verificationSuccess = null;
      try {
        const { data } = await InboxesAPI.verifyVitalpbxConnection(
          this.inbox.id
        );
        this.vitalpbxConfig.validationStatus = 'connected';
        this.vitalpbxConfig.verificationSuccess = true;
        this.vitalpbxConfig.verificationMessage =
          data.message ||
          this.$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.VERIFIED_SUCCESS');
      } catch (error) {
        this.vitalpbxConfig.validationStatus = 'error';
        this.vitalpbxConfig.verificationSuccess = false;
        this.vitalpbxConfig.verificationMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          this.$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.VERIFICATION_FAILED');
      } finally {
        this.vitalpbxConfig.isVerifying = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isAVitalpbxChannel" class="space-y-6">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.STATUS_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.STATUS_SUBHEADER')"
    >
      <div
        class="flex items-center justify-between p-4 bg-n-alpha-1 rounded-xl border border-n-strong"
      >
        <div class="flex items-center gap-2">
          <span
            class="inline-block size-3 rounded-full"
            :class="
              vitalpbxConfig.validationStatus === 'connected'
                ? 'bg-n-teal-9'
                : 'bg-n-amber-9'
            "
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{
              vitalpbxConfig.validationStatus === 'connected'
                ? $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.STATUS_CONNECTED')
                : vitalpbxConfig.validationStatus ||
                  $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.STATUS_PENDING')
            }}
          </span>
        </div>
        <NextButton
          outline
          slate
          size="sm"
          :is-loading="vitalpbxConfig.isVerifying"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.VERIFY_BUTTON')"
          @click="verifyVitalpbxConnection"
        />
      </div>
      <div
        v-if="vitalpbxConfig.verificationMessage"
        class="mt-2 p-3 rounded-lg text-sm"
        :class="
          vitalpbxConfig.verificationSuccess
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
        "
      >
        {{ vitalpbxConfig.verificationMessage }}
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.EXTENSION_NUMBER_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.EXTENSION_NUMBER_SUBHEADER')
      "
    >
      <woot-code
        :script="vitalpbxConfig.extensionNumber || inbox.extension_number"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.DEVICE_USER_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.DEVICE_USER_SUBHEADER')
      "
    >
      <woot-code :script="vitalpbxConfig.deviceUser" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.DEVICE_PASSWORD_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.DEVICE_PASSWORD_SUBHEADER')
      "
    >
      <div class="flex items-center gap-3">
        <woot-code :script="vitalpbxConfig.devicePassword" class="flex-1" />
        <NextButton
          outline
          slate
          size="sm"
          :label="
            vitalpbxConfig.isPasswordRevealed
              ? $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.HIDE_PASSWORD')
              : $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.REVEAL_PASSWORD')
          "
          @click="toggleRevealPassword"
        />
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.WSS_URL_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.WSS_URL_SUBHEADER')"
    >
      <woot-code :script="vitalpbxConfig.wssUrl" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.baseUrl"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.BASE_URL_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.BASE_URL_SUBHEADER')"
    >
      <woot-code :script="vitalpbxConfig.baseUrl" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.tenant"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.TENANT_TITLE')"
    >
      <woot-code :script="vitalpbxConfig.tenant" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.sipDomain"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.SIP_DOMAIN_TITLE')"
    >
      <woot-code :script="vitalpbxConfig.sipDomain" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.amiHost"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_HOST_TITLE')"
    >
      <woot-code :script="vitalpbxConfig.amiHost" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.amiPort"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_PORT_TITLE')"
    >
      <woot-code :script="String(vitalpbxConfig.amiPort)" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.amiUsername"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_USERNAME_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_USERNAME_SUBHEADER')
      "
    >
      <woot-code :script="vitalpbxConfig.amiUsername" />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="vitalpbxConfig.amiSecret"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_SECRET_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.AMI_SECRET_SUBHEADER')"
    >
      <div class="flex items-center gap-3">
        <woot-code :script="vitalpbxConfig.amiSecret" class="flex-1" />
        <NextButton
          outline
          slate
          size="sm"
          :label="
            vitalpbxConfig.isPasswordRevealed
              ? $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.HIDE_PASSWORD')
              : $t('INBOX_MGMT.SETTINGS_POPUP.VITALPBX.REVEAL_PASSWORD')
          "
          @click="toggleRevealPassword"
        />
      </div>
    </SettingsFieldSection>
  </div>

  <div v-else-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          v-if="showWhatsAppReconfigure"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')
          "
        >
          <NextButton
            :is-loading="isReconfiguring"
            :disabled="isReconfiguring"
            @click="reconfigureWhatsApp"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
          </NextButton>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else-if="!isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <WhatsappBusinessManagementToken
        v-if="
          isOnChatwootCloud &&
          inbox.provider === 'whatsapp_cloud' &&
          isEmbeddedSignupWhatsApp
        "
        :inbox="inbox"
      />
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </SettingsFieldSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  :deep(input) {
    margin-bottom: 0;
  }
}
</style>
